import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import Lightbox from "@/components/Lightbox";
import WallView from "@/components/WallView";
import StoryExperience from "@/components/StoryExperience";
import ReviewsSection, { RatingPill } from "@/components/ReviewsSection";
import { getYouTubeId } from "@/lib/youtube";
import { computeSalePrice, useSiteSpecial } from "@/lib/pricingHelpers";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useAuth } from "@/contexts/AuthContext";
import {
  Heart, Share2, Star, Facebook, Twitter,
  Link as LinkIcon, ChevronLeft, ChevronRight, QrCode, Maximize2, Frame, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

export default function ArtworkDetail() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const qrCode = search.get("qr");
  const [art, setArt] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [wallView, setWallView] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({ avg: 0, count: 0 });
  const siteSpecial = useSiteSpecial();
  const settings = useSiteSettings();
  const { user, refresh } = useAuth();

  useEffect(() => {
    http.get(`/artworks/${slug}`).then(({ data }) => {
      setArt(data);
      setActiveImg(0);
      // Load rating summary (uses artwork id from response)
      if (data?.id) {
        http.get(`/artworks/${data.id}/reviews/summary`)
          .then(({ data: s }) => setRatingSummary(s))
          .catch(() => {});
      }
    });
  }, [slug]);

  if (!art) {
    return <Layout><div className="min-h-[60vh] flex items-center justify-center font-ui text-[#6a6660] tracking-widest text-sm">Loading…</div></Layout>;
  }

  const isFav = !!user?.favorites?.includes(art.id);
  const variants = (art.variants || []).filter(v => v.type !== "upgrade" && v.active);
  const minPrice = variants.length ? Math.min(...variants.map(v => v.price)) : 0;
  const minSale = computeSalePrice(minPrice, art, siteSpecial);
  const images = [art.main_image, ...(art.additional_images || [])].filter(Boolean);
  const ytId = getYouTubeId(art.youtube_url);

  const toggleFav = async () => {
    if (!user) { toast.error("Sign in to save favorites"); return; }
    await http.post(`/favorites/${art.id}`);
    refresh();
  };

  const share = async (kind) => {
    const url = window.location.href;
    if (kind === "copy") {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
      return;
    }
    const text = encodeURIComponent(`${art.title} — Simple Truths Club`);
    const u = encodeURIComponent(url);
    const targets = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      twitter: `https://twitter.com/intent/tweet?url=${u}&text=${text}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${u}&description=${text}`,
    };
    window.open(targets[kind], "_blank", "noopener,noreferrer,width=600,height=600");
  };

  return (
    <Layout>
      {qrCode && (
        <div data-testid="qr-banner" className="bg-[#D4A547] text-black px-6 py-3 text-center font-ui text-[11px] tracking-[0.22em]">
          <QrCode size={14} className="inline -mt-0.5 mr-2" /> Mobile Gallery purchase — scanned from a Simple Truth in the wild.
        </div>
      )}

      <section id="buy" className="bg-black pt-8 pb-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/shop" className="font-ui text-[10px] tracking-[0.22em] text-[#9a9690] hover:text-[#D4A547]">
              <ChevronLeft size={14} className="inline -mt-0.5"/> Back to Collection
            </Link>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* IMAGE STACK */}
            <div className="lg:col-span-7">
              <div className="relative aspect-[4/5] bg-[#0a0a0a] overflow-hidden border border-white/10 group">
                <img
                  src={resolveImage(images[activeImg] || art.main_image)}
                  alt={art.title}
                  onClick={() => setLightbox(true)}
                  className="absolute inset-0 w-full h-full object-contain p-4 md:p-6 cursor-zoom-in"
                  data-testid="artwork-main-image"
                />
                <button
                  type="button"
                  onClick={() => setLightbox(true)}
                  className="absolute top-3 right-3 w-10 h-10 bg-black/60 hover:bg-[#C9A055] hover:text-black border border-white/10 text-[#F4F1EA] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Open lightbox"
                  data-testid="artwork-open-lightbox"
                >
                  <Maximize2 size={15}/>
                </button>
                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImg(Math.max(0, activeImg - 1))} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 border border-white/10 flex items-center justify-center text-[#F2EFE8] hover:text-[#D4A547]"><ChevronLeft size={16}/></button>
                    <button onClick={() => setActiveImg(Math.min(images.length - 1, activeImg + 1))} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 border border-white/10 flex items-center justify-center text-[#F2EFE8] hover:text-[#D4A547]"><ChevronRight size={16}/></button>
                  </>
                )}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`w-1.5 h-1.5 rounded-full ${i === activeImg ? "bg-[#D4A547]" : "bg-white/30"}`} />
                  ))}
                </div>
              </div>
              {/* Thumbnail strip */}
              {images.length > 1 && (
                <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2" data-testid="artwork-thumbs">
                  {images.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`aspect-square bg-[#0a0a0a] border ${i === activeImg ? "border-[#D4A547]" : "border-white/10 hover:border-white/30"} overflow-hidden`}
                    >
                      <img src={resolveImage(src)} alt="" className="w-full h-full object-contain p-1"/>
                    </button>
                  ))}
                </div>
              )}
              {lightbox && (
                <Lightbox
                  images={images}
                  initialIndex={activeImg}
                  alt={art.title}
                  onClose={() => setLightbox(false)}
                />
              )}
              {wallView && (
                <WallView
                  imageSrc={images[activeImg] || art.main_image}
                  alt={art.title}
                  onClose={() => setWallView(false)}
                />
              )}
            </div>

            {/* DETAILS (slim — purchase lives inside Story Experience below) */}
            <div className="lg:col-span-5">
              <h1 data-testid="artwork-title" className="font-display-saira text-3xl md:text-4xl text-[#F2EFE8] tracking-wide leading-tight">{art.title}</h1>
              <div className="flex items-baseline gap-4 mt-4">
                <div className="font-display-saira text-3xl text-[#D4A547]" data-testid="artwork-price-from">
                  <span className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#9a9690] mr-3 align-middle">From</span>
                  {minSale.isOnSale ? (
                    <span className="stc-sale-price inline-flex items-baseline gap-2 ml-1">
                      <span className="stc-sale-strike font-ui-tight text-xl text-[#6a6660] line-through decoration-[#d97a7a] decoration-[1.5px]">${minSale.original.toFixed(2)}</span>
                      <span className="stc-sale-effective text-3xl">${minSale.effective.toFixed(2)}<span className="text-base">+</span></span>
                      {minSale.pctOff > 0 && (
                        <span className="stc-sale-pct font-ui text-[9px] tracking-[0.22em] text-black bg-[#D4A547] px-1.5 py-0.5">{minSale.pctOff}% OFF</span>
                      )}
                    </span>
                  ) : (
                    <span className="ml-1">${minPrice.toFixed(2)}<span className="text-base text-[#9a9690]">+</span></span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <RatingPill
                    summary={ratingSummary}
                    onCountClick={() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })}
                  />
                </div>
              </div>
              {art.short_description && (
                <p className="font-body text-[15px] text-[#B5B0A7] mt-4 leading-relaxed">{art.short_description}</p>
              )}

              {art.sponsored_by?.name && (
                <div
                  className="mt-5 border border-[#C9A055]/40 bg-[#0a0a0a] px-4 py-3 flex items-center gap-3"
                  data-testid="artwork-sponsored-by"
                >
                  <div className="text-[#C9A055] text-xl leading-none">✦</div>
                  <div className="flex-1">
                    <div className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#C9A055] mb-0.5">Inspired By</div>
                    <div className="font-display text-[15px] text-[#F2EFE8] leading-tight">
                      {art.sponsored_by.name}
                      {art.sponsored_by.city ? (
                        <span className="text-[#8a857d] font-body italic ml-2 text-[13px]">{art.sponsored_by.city}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {settings.wall_view_enabled && (
                <button
                  type="button"
                  onClick={() => setWallView(true)}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors"
                  data-testid="artwork-wallview-btn"
                >
                  <Frame size={13} /> See it on a Wall
                </button>
              )}

              {/* Invitation to scroll into the Story Experience (where purchase lives) */}
              <a
                href="#own-this-piece"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("own-this-piece")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-8 group block border border-white/10 hover:border-[#C9A055] transition-colors p-5"
                data-testid="scroll-to-story-cta"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Read the Story</div>
                    <p className="font-body text-[14px] text-[#E8E0CC]">Discover the truth behind this piece, then choose how you&apos;d like to own it.</p>
                  </div>
                  <ChevronDown size={18} className="text-[#C9A055] group-hover:translate-y-0.5 transition-transform shrink-0"/>
                </div>
              </a>

              {/* Share */}
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                <span className="font-ui text-[10px] tracking-[0.32em] text-[#9a9690]">Share</span>
                <div className="flex items-center gap-3 text-[#9a9690]">
                  <button onClick={() => share("facebook")} className="hover:text-[#D4A547]" aria-label="Share on Facebook"><Facebook size={16}/></button>
                  <button onClick={() => share("twitter")} className="hover:text-[#D4A547]" aria-label="Share on Twitter"><Twitter size={16}/></button>
                  <button onClick={toggleFav} data-testid="pdp-fav-btn" className={isFav ? "text-[#D4A547]" : "hover:text-[#D4A547]"} aria-label="Favorite"><Heart size={16} fill={isFav ? "#D4A547" : "transparent"}/></button>
                  <button onClick={() => share("copy")} className="hover:text-[#D4A547]" aria-label="Copy link"><LinkIcon size={16}/></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ YouTube Video ============ */}
      {ytId && (
        <section className="bg-black py-16 border-t border-white/10 relative overflow-hidden">
          <div className="absolute inset-0 bg-[#C9A055] opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }}></div>
          <div className="max-w-[1000px] mx-auto px-6 relative z-10">
            <div className="text-center mb-10">
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-4">Behind the Truth</div>
              <h2 className="font-display-saira text-3xl md:text-4xl text-[#F2EFE8]">Hear from Mickey Himself</h2>
              <p className="font-body text-[#9a9690] mt-4 max-w-2xl mx-auto text-[15px]">
                Dive deeper into the inspiration, process, and the simple truth behind this artwork. 
                Watch the exclusive commentary from the artist.
              </p>
            </div>
            <div className="aspect-video bg-[#050505] border border-white/20 shadow-[0_0_40px_rgba(201,160,85,0.1)] p-2">
              <iframe 
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`} 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* ============ Story Experience — immersive narrative + interactive purchase ============ */}
      <div id="story">
        <StoryExperience key={art.id} artwork={art} qrCode={qrCode} />
      </div>

      {/* ============ Reviews ============ */}
      <section className="bg-[#050505] pb-24">
        <div className="container mx-auto px-4">
          <ReviewsSection artworkId={art.id} onSummaryChange={setRatingSummary} />
        </div>
      </section>
    </Layout>
  );
}
